import {
  FOC_TOUR_LABEL_HUB_COP,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import { IOC_CARRYOVER_DISPOSITION } from '../orgWorkspaceTour/orgWorkspaceTourSteps';

export const HUB_COP_TOUR_PARAM = 'hubCopTour';

/** Opens Hub Layers and starts the Common Operating Picture walkthrough. */
export const HUB_COP_TOUR_HREF = `/hub?openTab=Layers&${HUB_COP_TOUR_PARAM}=1`;

export type HubCopTourStepId =
  | 'sectionIntro'
  | 'hubModel'
  | 'cop001'
  | 'cop002'
  | 'cop003Zoom'
  | 'cop003Approval'
  | 'cop004'
  | 'cop005'
  | 'focFeedback';

export type HubCopTourStepDef = {
  id: HubCopTourStepId;
  /** Spec requirement id; section intros have none. */
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  /** Optional bullet list rendered under the description in the popover. */
  bullets?: string[];
  /** `data-tour` value. Missing targets become a centered popover. */
  tour?: string;
};

type RequirementInput = Omit<HubCopTourStepDef, 'title' | 'requirementId'> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): HubCopTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

/**
 * Walk order for 5.1 Hub Operating Model and 5.2 COP map data
 * (US-COP001–005). Source material reuses US-COP003 for zoom and approval;
 * step ids stay unique.
 */
export const HUB_COP_TOUR_STEPS: HubCopTourStepDef[] = [
  {
    id: 'sectionIntro',
    tour: 'hub-layers-workspace',
    title: '5. Common Operating Picture — The Hub',
    description:
      'The Hub is the enterprise Common Operating Picture: geospatial awareness and incident decision-support scaled by role, echelon, and assignment—not a single generic dashboard for every user.',
  },
  {
    id: 'hubModel',
    tour: 'hub-layers-workspace',
    title: '5.1 Hub Operating Model',
    description:
      'PRATUS Hub workspaces present organizational and incident context so users see the picture that matches their responsibilities.',
    bullets: [
      'Role and permission shape what layers, incidents, and actions are available.',
      'Echelon (Area, District, Sector, subunit) scopes awareness and roll-up.',
      'Incident assignment focuses the COP on active response; Hub enterprise layers remain available for broader awareness.',
    ],
  },
  requirement({
    id: 'cop001',
    requirementId: 'US-COP001',
    name: 'View Geospatial Objects',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'hub-cop-layer-row',
    description:
      'View geospatial objects drawn from external sources (including FSLTP/public ArcGIS), PRATUS-authored layers, and combined overlays on the Hub map.',
  }),
  requirement({
    id: 'cop002',
    requirementId: 'US-COP002',
    name: 'View Geospatial Object Metadata',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'hub-cop-layer-detail',
    description:
      'Inspect geospatial object metadata such as source system, values, and last updated so users can trust what they see on the map.',
  }),
  requirement({
    id: 'cop003Zoom',
    requirementId: 'US-COP003',
    name: 'Zoom to Attached Geographic Feature',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'hub-cop-layer-zoom',
    description:
      'Use the map control on a layer or feature to pan and zoom to its attached point, geofence, or extent.',
  }),
  requirement({
    id: 'cop003Approval',
    requirementId: 'US-COP003',
    name: 'Apply Approval Workflow to Data Layers',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'hub-cop-review-queue',
    description:
      'Propose edits and route layer versions through an approval workflow so published COP data reflects authorized changes.',
  }),
  requirement({
    id: 'cop004',
    requirementId: 'US-COP004',
    name: 'Assign Data Layers to an Incident',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'hub-cop-layer-incident',
    description:
      'Assign a data layer to an incident so the COP can scope overlays and editing to that response workspace.',
  }),
  requirement({
    id: 'cop005',
    requirementId: 'US-COP005',
    name: 'Use IMH ICS Icons and Symbology',
    disposition: IOC_CARRYOVER_DISPOSITION,
    tour: 'hub-cop-symbology',
    description:
      'Apply IMH / ICS icons and symbology when placing or editing geospatial features so map symbols stay consistent with Coast Guard doctrine.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_HUB_COP),
  },
];

/** Product Tours nav shows this walkthrough only when FOC requirements are on. */
export function hubCopTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function hubCopTourStepIds(): HubCopTourStepId[] {
  return HUB_COP_TOUR_STEPS.map((step) => step.id);
}

export function hubCopTourStepTitles(): string[] {
  return HUB_COP_TOUR_STEPS.map((step) => step.title);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Popover body: the disposition line first, then the requirement text. */
export function hubCopTourPopoverHtml(
  step: Pick<HubCopTourStepDef, 'disposition' | 'description' | 'bullets'>
): string {
  const body = `<p>${escapeHtml(step.description)}</p>`;
  const bullets = step.bullets?.length
    ? `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    : '';
  const content = `${body}${bullets}`;
  if (!step.disposition) return content;
  return `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>${content}`;
}
